/**
 * Generated by orval v8.20.0.
 * Do not edit manually.
 * Hub API v2.0.0.
 */

import type { HttpContext, HttpEvent, HttpParams } from '@angular/common/http';
import { HttpResponse as AngularHttpResponse, HttpClient, HttpHeaders } from '@angular/common/http';

import { Injectable, inject } from '@angular/core';

import { Observable } from 'rxjs';

import { environment } from '../../../environments/environment';

import type {
  AgencyAgenciesList200,
  AgencyAgenciesListParams,
  AgencyConsumerAgenciesList200,
  AgencyConsumerAgenciesListParams,
  AgencyGet200,
  AgencyRegister200,
  AgencyRegisterBody,
  AgencyRemoveLogo200,
  AgencyUpdate200,
  AgencyUpdateBody,
  AgencyUploadLogo200,
  AgencyUploadLogoBody,
} from '../../models';

interface HttpClientOptions {
  readonly headers?: HttpHeaders | Record<string, string | string[]>;
  readonly context?: HttpContext;
  readonly params?:
    | HttpParams
    | Record<string, string | number | boolean | Array<string | number | boolean>>;
  readonly reportProgress?: boolean;
  readonly withCredentials?: boolean;
  readonly credentials?: RequestCredentials;
  readonly keepalive?: boolean;
  readonly priority?: RequestPriority;
  readonly cache?: RequestCache;
  readonly mode?: RequestMode;
  readonly redirect?: RequestRedirect;
  readonly referrer?: string;
  readonly integrity?: string;
  readonly referrerPolicy?: ReferrerPolicy;
  readonly transferCache?: { includeHeaders?: string[] } | boolean;
  readonly timeout?: number;
}

type HttpClientBodyOptions = HttpClientOptions & {
  readonly observe?: 'body';
};

type HttpClientEventOptions = HttpClientOptions & {
  readonly observe: 'events';
};

type HttpClientResponseOptions = HttpClientOptions & {
  readonly observe: 'response';
};

type HttpClientObserveOptions = HttpClientOptions & {
  readonly observe?: 'body' | 'events' | 'response';
};

type AngularHttpParamValue = string | number | boolean | Array<string | number | boolean>;
type AngularHttpParamValueWithNullable = AngularHttpParamValue | null;

function filterParams(
  params: Record<string, unknown>,
  requiredNullableKeys?: ReadonlySet<string>,
  preserveRequiredNullables?: false,
  passthroughKeys?: undefined
): Record<string, AngularHttpParamValue>;
function filterParams(
  params: Record<string, unknown>,
  requiredNullableKeys: ReadonlySet<string> | undefined,
  preserveRequiredNullables: true,
  passthroughKeys?: undefined
): Record<string, AngularHttpParamValueWithNullable>;
function filterParams(
  params: Record<string, unknown>,
  requiredNullableKeys: ReadonlySet<string> | undefined,
  preserveRequiredNullables: boolean | undefined,
  passthroughKeys: ReadonlySet<string>
): Record<string, unknown>;
function filterParams(
  params: Record<string, unknown>,
  requiredNullableKeys: ReadonlySet<string> = new Set(),
  preserveRequiredNullables = false,
  passthroughKeys: ReadonlySet<string> = new Set()
): Record<string, unknown> {
  const filteredParams: Record<string, unknown> = {};
  for (const [key, value] of Object.entries(params)) {
    if (passthroughKeys.has(key)) {
      if (value !== undefined) {
        filteredParams[key] = value;
      }
      continue;
    }
    if (Array.isArray(value)) {
      const filtered = value.filter(
        item =>
          item != null &&
          (typeof item === 'string' || typeof item === 'number' || typeof item === 'boolean')
      ) as Array<string | number | boolean>;
      if (filtered.length) {
        filteredParams[key] = filtered;
      }
    } else if (preserveRequiredNullables && value === null && requiredNullableKeys.has(key)) {
      filteredParams[key] = null;
    } else if (
      value != null &&
      (typeof value === 'string' || typeof value === 'number' || typeof value === 'boolean')
    ) {
      filteredParams[key] = value;
    }
  }
  return filteredParams;
}

@Injectable({ providedIn: 'root' })
export class AgenciesService {
  private readonly http = inject(HttpClient);
  /**
   * Returns the session's agency: its contact details, branding and what it is allowed to do. Managers of the agency only.<br><br>Replaces `GET /v1.5/agent/account/data` of the legacy agent API: `agency_image_url` is `logo_url` and `address` is `street`.
   * @summary Get the agency
   */
  agencyGet<TData = AgencyGet200>(options?: HttpClientBodyOptions): Observable<TData>;
  agencyGet<TData = AgencyGet200>(options?: HttpClientEventOptions): Observable<HttpEvent<TData>>;
  agencyGet<TData = AgencyGet200>(
    options?: HttpClientResponseOptions
  ): Observable<AngularHttpResponse<TData>>;
  agencyGet<TData = AgencyGet200>(
    options?: HttpClientObserveOptions
  ): Observable<TData | HttpEvent<TData> | AngularHttpResponse<TData>> {
    if (options?.observe === 'events') {
      return this.http.get<TData>(`${environment.hubApiUrl}/agency`, {
        ...(options as Omit<NonNullable<typeof options>, 'observe'>),
        observe: 'events',
      });
    }

    if (options?.observe === 'response') {
      return this.http.get<TData>(`${environment.hubApiUrl}/agency`, {
        ...(options as Omit<NonNullable<typeof options>, 'observe'>),
        observe: 'response',
      });
    }

    return this.http.get<TData>(`${environment.hubApiUrl}/agency`, {
      ...(options as Omit<NonNullable<typeof options>, 'observe'>),
      observe: 'body',
    });
  }
  /**
   * Applies a sparse edit to the session agency's own contact and branding details and returns the agency. A key that is absent is left alone; a key present and `null` clears the field, and a cleared `brand_color` is the default `#A70000` again. Managers of the agency only.<br>The editable fields are those of `PATCH /agw/agencies/{agency_id}`, and the two routes are meant to stay in step: the consumer, the name, whether the agency is active, whether it may book or issue, and the email-domain whitelist are not the agency's to set.<br><br>Replaces `POST /v1.5/agent/account/data` of the legacy agent API: the write is sparse rather than the whole form, `address` is `street`, and the reply is the agency.
   * @summary Update the agency's contact details
   */
  agencyUpdate<TData = AgencyUpdate200>(
    agencyUpdateBody: AgencyUpdateBody,
    options?: HttpClientBodyOptions
  ): Observable<TData>;
  agencyUpdate<TData = AgencyUpdate200>(
    agencyUpdateBody: AgencyUpdateBody,
    options?: HttpClientEventOptions
  ): Observable<HttpEvent<TData>>;
  agencyUpdate<TData = AgencyUpdate200>(
    agencyUpdateBody: AgencyUpdateBody,
    options?: HttpClientResponseOptions
  ): Observable<AngularHttpResponse<TData>>;
  agencyUpdate<TData = AgencyUpdate200>(
    agencyUpdateBody: AgencyUpdateBody,
    options?: HttpClientObserveOptions
  ): Observable<TData | HttpEvent<TData> | AngularHttpResponse<TData>> {
    if (options?.observe === 'events') {
      return this.http.patch<TData>(`${environment.hubApiUrl}/agency`, agencyUpdateBody, {
        ...(options as Omit<NonNullable<typeof options>, 'observe'>),
        observe: 'events',
      });
    }

    if (options?.observe === 'response') {
      return this.http.patch<TData>(`${environment.hubApiUrl}/agency`, agencyUpdateBody, {
        ...(options as Omit<NonNullable<typeof options>, 'observe'>),
        observe: 'response',
      });
    }

    return this.http.patch<TData>(`${environment.hubApiUrl}/agency`, agencyUpdateBody, {
      ...(options as Omit<NonNullable<typeof options>, 'observe'>),
      observe: 'body',
    });
  }
  /**
   * Returns a paginated list of the active agencies the logged-in agent belongs to, ordered by name. Optional name and role filters narrow the results. The session is resolved for the agent alone and the `ag-agency` header is ignored, so an agent whose default agency has been deactivated can still list the agencies they may switch to.<br><br>Replaces `GET /v1.5/agent/account/agencies` of the legacy agent API: an envelope with pagination metadata rather than a bare list; `image_url` is `logo_url`; `superagency` is new; `agent_id`, `status` and `is_default_agency` are gone, inactive agencies no longer being listed.
   * @summary List agencies
   */
  agencyAgenciesList<TData = AgencyAgenciesList200>(
    params?: AgencyAgenciesListParams,
    options?: HttpClientBodyOptions
  ): Observable<TData>;
  agencyAgenciesList<TData = AgencyAgenciesList200>(
    params?: AgencyAgenciesListParams,
    options?: HttpClientEventOptions
  ): Observable<HttpEvent<TData>>;
  agencyAgenciesList<TData = AgencyAgenciesList200>(
    params?: AgencyAgenciesListParams,
    options?: HttpClientResponseOptions
  ): Observable<AngularHttpResponse<TData>>;
  agencyAgenciesList<TData = AgencyAgenciesList200>(
    params?: AgencyAgenciesListParams,
    options?: HttpClientObserveOptions
  ): Observable<TData | HttpEvent<TData> | AngularHttpResponse<TData>> {
    const filteredParams = filterParams({ ...params, ...options?.params }, new Set<string>([]));

    if (options?.observe === 'events') {
      return this.http.get<TData>(`${environment.hubApiUrl}/agency/agencies`, {
        ...(options as Omit<NonNullable<typeof options>, 'observe'>),
        observe: 'events',
        params: filteredParams,
      });
    }

    if (options?.observe === 'response') {
      return this.http.get<TData>(`${environment.hubApiUrl}/agency/agencies`, {
        ...(options as Omit<NonNullable<typeof options>, 'observe'>),
        observe: 'response',
        params: filteredParams,
      });
    }

    return this.http.get<TData>(`${environment.hubApiUrl}/agency/agencies`, {
      ...(options as Omit<NonNullable<typeof options>, 'observe'>),
      observe: 'body',
      params: filteredParams,
    });
  }
  /**
   * Returns a paginated list of every agency of the session's consumer, the super agency included and flagged as `superagency`, ordered by name. Withdrawn sub-agencies are listed too unless `active` says otherwise, so they can still be reported on. An optional name filter narrows the results, and a page holds up to a hundred agencies so a filter dropdown can read the whole set at once. Managers of the consumer's super agency only.<br><br>Replaces `GET /v1.5/agent/access/agency/list?include_superagency=true` of the legacy agent API: an envelope with pagination metadata rather than a bare list, and `include_accounts` is gone: an agency's agents are `GET /agency/agents` under that agency's `ag-agency`.
   * @summary List the consumer's agencies
   */
  agencyConsumerAgenciesList<TData = AgencyConsumerAgenciesList200>(
    params?: AgencyConsumerAgenciesListParams,
    options?: HttpClientBodyOptions
  ): Observable<TData>;
  agencyConsumerAgenciesList<TData = AgencyConsumerAgenciesList200>(
    params?: AgencyConsumerAgenciesListParams,
    options?: HttpClientEventOptions
  ): Observable<HttpEvent<TData>>;
  agencyConsumerAgenciesList<TData = AgencyConsumerAgenciesList200>(
    params?: AgencyConsumerAgenciesListParams,
    options?: HttpClientResponseOptions
  ): Observable<AngularHttpResponse<TData>>;
  agencyConsumerAgenciesList<TData = AgencyConsumerAgenciesList200>(
    params?: AgencyConsumerAgenciesListParams,
    options?: HttpClientObserveOptions
  ): Observable<TData | HttpEvent<TData> | AngularHttpResponse<TData>> {
    const filteredParams = filterParams({ ...params, ...options?.params }, new Set<string>([]));

    if (options?.observe === 'events') {
      return this.http.get<TData>(`${environment.hubApiUrl}/agency/consumer/agencies`, {
        ...(options as Omit<NonNullable<typeof options>, 'observe'>),
        observe: 'events',
        params: filteredParams,
      });
    }

    if (options?.observe === 'response') {
      return this.http.get<TData>(`${environment.hubApiUrl}/agency/consumer/agencies`, {
        ...(options as Omit<NonNullable<typeof options>, 'observe'>),
        observe: 'response',
        params: filteredParams,
      });
    }

    return this.http.get<TData>(`${environment.hubApiUrl}/agency/consumer/agencies`, {
      ...(options as Omit<NonNullable<typeof options>, 'observe'>),
      observe: 'body',
      params: filteredParams,
    });
  }
  /**
   * Opens an agency for somebody who has none and invites their email address as its manager, which creates their account and mails them the invitation; they sign in once they have accepted it. The agency is opened live and able to book and issue under the consumer the platform keeps for registrations, named after the first label of the address's domain with everything but letters and digits removed (`mara@nordic-desk.com` opens `nordicdesk`), with the address as its contact and operational address and the form's details. An address the platform already knows is refused rather than given the seat, so somebody with an account asks an agency's manager to be invited instead; a second registration from the same domain is refused for the name it would take. Nothing is written if any step is refused. Unauthenticated, and present only where the platform names a registration consumer: production names none, so there this route does not exist. It is not rate limited.<br><br>Replaces `POST /v1.5/agent/register-agency` of the legacy agent API: `address` is `street` and `phone` is `phone_contact`; `country_code`, `username`, `agency` and `iata_number` are gone; an address already registered, a taken name and a full consumer are 409 rather than 200 with an `error` map, and a domain that gives no name is 422; the reply is the agency and its manager rather than session fields, and nothing is left behind when a step fails.
   * @summary Register an agency
   */
  agencyRegister<TData = AgencyRegister200>(
    agencyRegisterBody: AgencyRegisterBody,
    options?: HttpClientBodyOptions
  ): Observable<TData>;
  agencyRegister<TData = AgencyRegister200>(
    agencyRegisterBody: AgencyRegisterBody,
    options?: HttpClientEventOptions
  ): Observable<HttpEvent<TData>>;
  agencyRegister<TData = AgencyRegister200>(
    agencyRegisterBody: AgencyRegisterBody,
    options?: HttpClientResponseOptions
  ): Observable<AngularHttpResponse<TData>>;
  agencyRegister<TData = AgencyRegister200>(
    agencyRegisterBody: AgencyRegisterBody,
    options?: HttpClientObserveOptions
  ): Observable<TData | HttpEvent<TData> | AngularHttpResponse<TData>> {
    if (options?.observe === 'events') {
      return this.http.post<TData>(`${environment.hubApiUrl}/agency/register`, agencyRegisterBody, {
        ...(options as Omit<NonNullable<typeof options>, 'observe'>),
        observe: 'events',
      });
    }

    if (options?.observe === 'response') {
      return this.http.post<TData>(`${environment.hubApiUrl}/agency/register`, agencyRegisterBody, {
        ...(options as Omit<NonNullable<typeof options>, 'observe'>),
        observe: 'response',
      });
    }

    return this.http.post<TData>(`${environment.hubApiUrl}/agency/register`, agencyRegisterBody, {
      ...(options as Omit<NonNullable<typeof options>, 'observe'>),
      observe: 'body',
    });
  }
  /**
   * Deletes the session agency's stored logo, clears its logo URL and returns the agency. Idempotent: an agency with no logo is already the state asked for, so this answers 200. Managers of the agency only.<br><br>Replaces `POST /v1.5/agent/account/remove-logo` of the legacy agent API: the reply is the agency.
   * @summary Remove the agency's logo
   */
  agencyRemoveLogo<TData = AgencyRemoveLogo200>(options?: HttpClientBodyOptions): Observable<TData>;
  agencyRemoveLogo<TData = AgencyRemoveLogo200>(
    options?: HttpClientEventOptions
  ): Observable<HttpEvent<TData>>;
  agencyRemoveLogo<TData = AgencyRemoveLogo200>(
    options?: HttpClientResponseOptions
  ): Observable<AngularHttpResponse<TData>>;
  agencyRemoveLogo<TData = AgencyRemoveLogo200>(
    options?: HttpClientObserveOptions
  ): Observable<TData | HttpEvent<TData> | AngularHttpResponse<TData>> {
    if (options?.observe === 'events') {
      return this.http.post<TData>(`${environment.hubApiUrl}/agency/remove-logo`, undefined, {
        ...(options as Omit<NonNullable<typeof options>, 'observe'>),
        observe: 'events',
      });
    }

    if (options?.observe === 'response') {
      return this.http.post<TData>(`${environment.hubApiUrl}/agency/remove-logo`, undefined, {
        ...(options as Omit<NonNullable<typeof options>, 'observe'>),
        observe: 'response',
      });
    }

    return this.http.post<TData>(`${environment.hubApiUrl}/agency/remove-logo`, undefined, {
      ...(options as Omit<NonNullable<typeof options>, 'observe'>),
      observe: 'body',
    });
  }
  /**
   * Stores a PNG or JPEG image (at most 200x200 and 1 MB), sent as the `logo` field of a multipart form, as the session agency's logo and returns the agency carrying its URL. The URL ends in a timestamp, so a replaced logo is not served from a cache. Managers of the agency only.<br><br>Replaces `POST /v1.5/agent/account/upload-logo` of the legacy agent API: the form field `file` is `logo`, and the reply is the agency.
   * @summary Upload the agency's logo
   */
  agencyUploadLogo<TData = AgencyUploadLogo200>(
    agencyUploadLogoBody?: AgencyUploadLogoBody,
    options?: HttpClientBodyOptions
  ): Observable<TData>;
  agencyUploadLogo<TData = AgencyUploadLogo200>(
    agencyUploadLogoBody?: AgencyUploadLogoBody,
    options?: HttpClientEventOptions
  ): Observable<HttpEvent<TData>>;
  agencyUploadLogo<TData = AgencyUploadLogo200>(
    agencyUploadLogoBody?: AgencyUploadLogoBody,
    options?: HttpClientResponseOptions
  ): Observable<AngularHttpResponse<TData>>;
  agencyUploadLogo<TData = AgencyUploadLogo200>(
    agencyUploadLogoBody?: AgencyUploadLogoBody,
    options?: HttpClientObserveOptions
  ): Observable<TData | HttpEvent<TData> | AngularHttpResponse<TData>> {
    const formData = new FormData();
    if (agencyUploadLogoBody?.logo !== undefined) {
      formData.append(`logo`, agencyUploadLogoBody.logo);
    }

    if (options?.observe === 'events') {
      return this.http.post<TData>(`${environment.hubApiUrl}/agency/upload-logo`, formData, {
        ...(options as Omit<NonNullable<typeof options>, 'observe'>),
        observe: 'events',
      });
    }

    if (options?.observe === 'response') {
      return this.http.post<TData>(`${environment.hubApiUrl}/agency/upload-logo`, formData, {
        ...(options as Omit<NonNullable<typeof options>, 'observe'>),
        observe: 'response',
      });
    }

    return this.http.post<TData>(`${environment.hubApiUrl}/agency/upload-logo`, formData, {
      ...(options as Omit<NonNullable<typeof options>, 'observe'>),
      observe: 'body',
    });
  }
}

export type AgencyGetClientResult = NonNullable<AgencyGet200>;
export type AgencyUpdateClientResult = NonNullable<AgencyUpdate200>;
export type AgencyAgenciesListClientResult = NonNullable<AgencyAgenciesList200>;
export type AgencyConsumerAgenciesListClientResult = NonNullable<AgencyConsumerAgenciesList200>;
export type AgencyRegisterClientResult = NonNullable<AgencyRegister200>;
export type AgencyRemoveLogoClientResult = NonNullable<AgencyRemoveLogo200>;
export type AgencyUploadLogoClientResult = NonNullable<AgencyUploadLogo200>;
